import { useEffect } from "react";
import { useNavigate } from "react-router-dom";
import {
  CommandDialog,
  CommandInput,
  CommandList,
  CommandEmpty,
  CommandGroup,
  CommandItem,
} from "@/components/ui/command";
import { useUiStore } from "@/store/uiStore";
import { useAuth } from "@/contexts/AuthContext";
import { NAV_GROUPS, ADMIN_NAV, ACCOUNT_NAV, NavItem } from "@/shared/nav";
import { isAdminUser, canUser } from "@/shared/rbac";
import { useLabels } from "@/lib/relabel";
import { useVertical } from "@/lib/useVertical";

export function CommandPalette() {
  const open = useUiStore((s) => s.paletteOpen);
  const setOpen = useUiStore((s) => s.setPaletteOpen);
  const navigate = useNavigate();
  const { user } = useAuth();
  const admin = isAdminUser(user);
  const { nav } = useLabels();
  const { id: vertical } = useVertical();

  useEffect(() => {
    const down = (e: KeyboardEvent) => {
      if (e.key === "k" && (e.metaKey || e.ctrlKey)) {
        e.preventDefault();
        setOpen(!open);
      }
    };
    document.addEventListener("keydown", down);
    return () => document.removeEventListener("keydown", down);
  }, [open, setOpen]);

  const go = (to: string) => {
    setOpen(false);
    navigate(to);
  };

  const renderItem = (item: NavItem) => {
    if (item.perm && !canUser(user, item.perm)) return null;
    if ((item.excludeVerticals || []).includes(vertical)) return null;
    const Icon = item.icon;
    return (
      <CommandItem
        key={item.to}
        value={`${item.label} ${item.keywords || ""}`}
        onSelect={() => go(item.to)}
        data-testid={`palette-item-${item.to.replace("/", "")}`}
        className="gap-2"
      >
        <Icon className="h-4 w-4 text-muted-foreground" />
        {nav(item.label)}
      </CommandItem>
    );
  };

  return (
    <CommandDialog open={open} onOpenChange={setOpen}>
      <CommandInput placeholder="Search pages, tools and settings…" data-testid="command-input" />
      <CommandList>
        <CommandEmpty>No results found.</CommandEmpty>
        {NAV_GROUPS.map((group) => (
          <CommandGroup key={group.label} heading={group.label}>
            {group.items.map(renderItem)}
          </CommandGroup>
        ))}
        <CommandGroup heading="Account">{ACCOUNT_NAV.map(renderItem)}</CommandGroup>
        {admin && <CommandGroup heading="Administration">{ADMIN_NAV.map(renderItem)}</CommandGroup>}
      </CommandList>
    </CommandDialog>
  );
}

export default CommandPalette;
